import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

const KasbonForm = ({ isModalOpen, onClose }) => {
    const [keperluan, setKeperluan] = useState("");
    const [nominal, setNominal] = useState("");
    const [periodeMulai, setPeriodeMulai] = useState("");

    // Canvas TTD Pemohon
    const canvasRefPemohon = useRef(null);
    const isDrawingPemohon = useRef(false);
    const ctxRefPemohon = useRef(null);

    const styles = {
        field: { marginBottom: 16 },
        label: { display: "block", marginBottom: 8, fontWeight: 500 },
        input: {
            width: "100%",
            padding: "12px",
            borderRadius: 12,
            border: "1.5px solid #ddd",
            fontSize: 15,
            backgroundColor: "#fff",
            outline: "none",
            boxSizing: "border-box",
        },
    };

    // ── Helper Periode (F Y) ──
    const generatePeriodeOptions = () => {
        const options = [];
        const now = new Date();
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        let currentMonth = now.getMonth();
        let currentYear = now.getFullYear();

        // Generate 6 bulan ke depan
        for (let i = 0; i < 6; i++) {
            options.push(`${monthNames[currentMonth]} ${currentYear}`);
            currentMonth++;
            if (currentMonth > 11) {
                currentMonth = 0;
                currentYear++;
            }
        }
        return options;
    };

    const periodeOptions = useRef(generatePeriodeOptions()).current;

    const getKaryawanId = () => {
        try { return JSON.parse(localStorage.getItem("data_karyawan"))?.id || null; }
        catch { return null; }
    };

    const formatRupiah = (angka) => {
        const value = angka.toString().replace(/[^,\d]/g, '');
        return Number(value).toLocaleString("id-ID");
    };

    const handleNominalChange = (e) => {
        const rawValue = e.target.value.replace(/[^,\d]/g, '');
        setNominal(rawValue);
    };

    // ── Setup canvas ──
    useEffect(() => {
        if (!isModalOpen) return;

        const setupCanvas = (ref, ctxRef) => {
            if (!ref.current) return;
            const canvas = ref.current;
            const ctx = canvas.getContext("2d");
            ctx.lineCap = "round";
            ctx.strokeStyle = "#333";
            ctx.lineWidth = 3;
            ctxRef.current = ctx;
            const resize = () => {
                const { width } = canvas.getBoundingClientRect();
                canvas.width = width * window.devicePixelRatio;
                canvas.height = 150 * window.devicePixelRatio;
                ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
            };
            resize();
            return resize;
        };

        const resizePemohon = setupCanvas(canvasRefPemohon, ctxRefPemohon);

        const handleResize = () => {
            if (resizePemohon) resizePemohon();
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isModalOpen]);

    // Reset isi form
    useEffect(() => {
        if (isModalOpen && canvasRefPemohon.current) {
            canvasRefPemohon.current.getContext("2d")?.clearRect(0, 0, canvasRefPemohon.current.width, canvasRefPemohon.current.height);
        }
        if (!isModalOpen) {
            setKeperluan("");
            setNominal("");
            setPeriodeMulai("");
        }
    }, [isModalOpen]);

    // ── Drawing handlers ──
    const getPos = (ref, e, isTouch) => {
        const rect = ref.current.getBoundingClientRect();
        return {
            x: (isTouch ? e.touches[0].clientX : e.clientX) - rect.left,
            y: (isTouch ? e.touches[0].clientY : e.clientY) - rect.top,
        };
    };

    const handleDrawStart = (target, ref, ctxRef, e, isTouch = false) => {
        target.current = true;
        const { x, y } = getPos(ref, e, isTouch);
        ctxRef.current.beginPath();
        ctxRef.current.moveTo(x, y);
    };

    const handleDrawMove = (target, ref, ctxRef, e, isTouch = false) => {
        if (!target.current) return;
        const { x, y } = getPos(ref, e, isTouch);
        ctxRef.current.lineTo(x, y);
        ctxRef.current.stroke();
    };

    const handleDrawEnd = (target) => { target.current = false; };

    const clearSignature = (ref) => {
        if (!ref.current) return;
        const ctx = ref.current.getContext("2d");
        ctx?.clearRect(0, 0, ref.current.width, ref.current.height);
    };

    // ── Submit ──
    const handleSubmit = async () => {
        const karyawanId = getKaryawanId();
        if (!karyawanId) {
            await Swal.fire({ icon: "warning", title: "Data Diri Kosong", text: "Silahkan login ulang." });
            return;
        }
        if (!keperluan || !nominal || !periodeMulai) {
            await Swal.fire({ icon: "warning", title: "Form Belum Lengkap", text: "Semua isian wajib diisi." });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Ajukan Kasbon?",
            text: `Anda akan mengajukan kasbon sebesar Rp ${formatRupiah(nominal)}`,
            showCancelButton: true,
            confirmButtonText: "Ya, Ajukan",
            cancelButtonText: "Batal",
        });
        if (!confirm.isConfirmed) return;

        const formData = new FormData();
        formData.append("karyawan_id", karyawanId);
        formData.append("absen_id", "kasbon");
        formData.append("periode", periodeMulai); // Pakai periode potongan mulai
        formData.append("nama", keperluan);
        formData.append("nominal", nominal);

        if (canvasRefPemohon.current) {
            formData.append("ttd_pemohon", canvasRefPemohon.current.toDataURL("image/png"));
        }

        // Tampilkan success langsung (Background processing)
        Swal.fire({ icon: "success", title: "Berhasil!", text: "Pengajuan kasbon sedang diproses...", timer: 1500, showConfirmButton: false });
        onClose?.();

        // Proses API di background
        fetch("https://jne-cilacap.com/slip/public/api/uang", { method: "POST", body: formData })
            .then(async (res) => {
                if (!res.ok) throw new Error(await res.text());
                console.log("Submit kasbon sukses (background)");
            })
            .catch(err => {
                console.error("Gagal mengirim kasbon (background):", err.message);
                Swal.fire({ icon: "error", title: "Gagal Mengirim", text: "Proses kasbon di latar belakang gagal: " + err.message });
            });
    };

    return (
        <div style={{ width: "100%", boxSizing: "border-box" }}>
            <div style={styles.field}>
                <label style={styles.label}>Keperluan Kasbon:</label>
                <textarea
                    value={keperluan}
                    onChange={e => setKeperluan(e.target.value)}
                    placeholder="Contoh: Biaya perbaikan kendaraan operasional"
                    rows={3}
                    style={{ ...styles.input, resize: "none" }}
                />
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Nominal (Rp):</label>
                <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 14, top: 12, color: "#666", fontWeight: 500 }}>Rp</span>
                    <input
                        type="text"
                        value={nominal ? formatRupiah(nominal) : ""}
                        onChange={handleNominalChange}
                        placeholder="0"
                        style={{ ...styles.input, paddingLeft: 42 }}
                    />
                </div>
            </div>

            <div style={styles.field}>
                <label style={styles.label}>Pemotongan Mulai Bulan:</label>
                <select
                    value={periodeMulai}
                    onChange={e => setPeriodeMulai(e.target.value)}
                    style={styles.input}
                >
                    <option value="">Pilih periode pemotongan...</option>
                    {periodeOptions.map((opt, idx) => (
                        <option key={idx} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>

            {/* TTD Pemohon */}
            <div style={styles.field}>
                <label style={styles.label}>Tanda Tangan Pemohon:</label>
                <canvas
                    ref={canvasRefPemohon}
                    width={300} height={150}
                    style={{ border: "1.5px dashed #ccc", borderRadius: 12, width: "100%", height: 150, backgroundColor: "#f9f9f9", touchAction: "none" }}
                    onMouseDown={(e) => handleDrawStart(isDrawingPemohon, canvasRefPemohon, ctxRefPemohon, e)}
                    onMouseMove={(e) => handleDrawMove(isDrawingPemohon, canvasRefPemohon, ctxRefPemohon, e)}
                    onMouseUp={() => handleDrawEnd(isDrawingPemohon)}
                    onMouseLeave={() => handleDrawEnd(isDrawingPemohon)}
                    onTouchStart={e => { e.preventDefault(); handleDrawStart(isDrawingPemohon, canvasRefPemohon, ctxRefPemohon, e, true); }}
                    onTouchMove={e => { e.preventDefault(); handleDrawMove(isDrawingPemohon, canvasRefPemohon, ctxRefPemohon, e, true); }}
                    onTouchEnd={() => handleDrawEnd(isDrawingPemohon)}
                />
                <button onClick={() => clearSignature(canvasRefPemohon)} style={{ marginTop: 8, color: "#dc2626", background: "none", border: "none", fontSize: 13, cursor: "pointer" }}>Hapus Tanda Tangan</button>
            </div>

            <button
                onClick={handleSubmit}
                style={{ width: "100%", padding: "14px", backgroundColor: "#2563eb", color: "#fff", fontSize: 16, fontWeight: 600, border: "none", borderRadius: 16, cursor: "pointer", marginTop: 4 }}
            >
                Ajukan Kasbon
            </button>
        </div>
    );
};

export default KasbonForm;
